'use client'

import { useState } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { usePermissions } from '@/hooks/use-permissions'
import { usePlanFeatureAccessQuery } from '@/hooks/queries/plan-feature-access'
import { PBX_PERMISSIONS, INTEGRATIONS_PERMISSIONS, VOICE_AGENTS_PERMISSIONS } from '@/lib/auth/permissions.constants'
import { useTwilioIntegration } from '@/hooks/integrations/use-twilio-integration'
import { useVoiceAgents } from '@/hooks/agents/voice'
import { TwilioCredentialsForm } from '@/components/integrations/twilio'
import { OutgoingCallDialog } from '@/components/agents/voice/outgoing-call-dialog'
import { TwilioConnectionBanner } from '../twilio-connection-banner'
import { OverviewTab } from './OverviewTab'
import { PhoneNumbersTab } from './PhoneNumbersTab'
import { CallFlowsTab } from './CallFlowsTab'
import { OperatorGroupsTab } from './OperatorGroupsTab'
import { VoicemailsTab } from './VoicemailsTab'
import { FlowEditorView } from './FlowEditorView'
import { FlowWizard } from '../wizard/FlowWizard'
import { Loader2, Settings, CheckCircle2, Plus } from 'lucide-react'
import { useTranslations } from "next-intl"


type PbxTab = 'overview' | 'numbers' | 'flows' | 'operators' | 'voicemails'

export function PbxPage() {
  const tUi = useTranslations("Pbx.PbxPagesPbxPage")

  const router = useRouter()
  const searchParams = useSearchParams()
  const { can } = usePermissions()
  const { integration, isLoading: isLoadingTwilio, isConfigured, refreshIntegration } = useTwilioIntegration()
  const { agents: voiceAgents, isLoading: isLoadingVoiceAgents, error: voiceAgentsError } = useVoiceAgents()

  // Key for tabs - changes when integration changes, forcing tabs to reload data
  const integrationKey = integration?.id || 'no-integration'

  const canManagePbx = can(PBX_PERMISSIONS.MANAGE)
  const canViewIntegrations = can(INTEGRATIONS_PERMISSIONS.VIEW)
  const canManageCampaigns = can(VOICE_AGENTS_PERMISSIONS.MANAGE)
  const campaignsLocked = usePlanFeatureAccessQuery().data?.campaigns.available === false

  // Check for flow editor/wizard mode
  const editFlowId = searchParams.get('flowId')
  const isNewFlow = searchParams.get('new') === 'true'
  const useAdvancedEditor = searchParams.get('advanced') === 'true'

  // Tab state
  const tabParam = searchParams.get('tab') as PbxTab | null
  const [activeTab, setActiveTab] = useState<PbxTab>(tabParam || 'overview')
  const [isTwilioSettingsOpen, setIsTwilioSettingsOpen] = useState(false)
  const [isOutgoingCallOpen, setIsOutgoingCallOpen] = useState(false)
  const [operatorGroupCreateRequestId, setOperatorGroupCreateRequestId] = useState(0)

  const handleTabChange = (value: string) => {
    setActiveTab(value as PbxTab)
    const url = new URL(window.location.href)
    url.searchParams.set('tab', value)
    url.searchParams.delete('flowId')
    url.searchParams.delete('new')
    window.history.replaceState({}, '', url.toString())
  }

  const handleEditFlow = (flowId: string) => {
    const url = new URL(window.location.href)
    url.searchParams.set('flowId', flowId)
    url.searchParams.delete('new')
    window.history.pushState({}, '', url.toString())
    router.refresh()
  }

  const handleNewFlow = () => {
    const url = new URL(window.location.href)
    url.searchParams.set('new', 'true')
    url.searchParams.delete('flowId')
    window.history.pushState({}, '', url.toString())
    router.refresh()
  }

  const handleNewOperatorGroup = () => {
    setOperatorGroupCreateRequestId((requestId) => requestId + 1)
  }

  const handleNewCampaign = () => {
    router.push('?page=campaigns&campaignId=new')
  }

  const handleMakeCall = () => {
    setIsOutgoingCallOpen(true)
  }

  const handleViewAllCalls = () => {
    router.push('?page=call-logs')
  }

  const handleImportExternalSip = () => {
    setActiveTab('numbers')
    const url = new URL(window.location.href)
    url.searchParams.set('tab', 'numbers')
    url.searchParams.set('importSip', 'true')
    url.searchParams.delete('flowId')
    url.searchParams.delete('new')
    window.history.replaceState({}, '', url.toString())
  }

  const handleCloseEditor = () => {
    const url = new URL(window.location.href)
    url.searchParams.delete('flowId')
    url.searchParams.delete('new')
    url.searchParams.delete('advanced')
    window.history.pushState({}, '', url.toString())
    router.refresh()
  }

  const handleOpenAdvancedEditor = () => {
    const url = new URL(window.location.href)
    url.searchParams.set('advanced', 'true')
    window.history.replaceState({}, '', url.toString())
    router.refresh()
  }

  const handleWizardComplete = (_flowId: string) => {
    // After wizard completes, close and return to flows list
    // The flow is already saved and activated by the wizard
    handleCloseEditor()
    setActiveTab('flows')
  }

  // If editing an existing flow, always show the editor
  if (editFlowId) {
    return (
      <FlowEditorView
        flowId={editFlowId}
        isNew={false}
        onClose={handleCloseEditor}
      />
    )
  }

  // If creating a new flow
  if (isNewFlow) {
    // Show advanced editor if requested, otherwise show wizard
    if (useAdvancedEditor) {
      return (
        <FlowEditorView
          flowId={null}
          isNew={true}
          onClose={handleCloseEditor}
        />
      )
    }

    // Show wizard for new flows
    return (
      <div className="mx-auto max-w-7xl p-4 sm:p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-foreground">
            <span key={tUi("createInboundCallFlow")}>{tUi("createInboundCallFlow")}</span></h1>
          <p className="text-muted-foreground mt-1">
            <span key={tUi("setUpHowIncomingCallsAreHandled")}>{tUi("setUpHowIncomingCallsAreHandled")}</span></p>
        </div>
        <FlowWizard
          onComplete={handleWizardComplete}
          onCancel={handleCloseEditor}
          onOpenAdvanced={handleOpenAdvancedEditor}
        />
      </div>
    )
  }

  // Show loading state while checking Twilio integration
  if (isLoadingTwilio) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const handleTwilioConfigured = () => {
    setIsTwilioSettingsOpen(false)
    refreshIntegration()
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            <span key={tUi("phoneNumbers")}>{tUi("phoneNumbers")}</span></h1>
          <p className="text-muted-foreground mt-1">
            <span key={tUi("managePhoneNumbersInboundCallFlowsAndRouting")}>{tUi("managePhoneNumbersInboundCallFlowsAndRouting")}</span></p>
        </div>

        {/* Twilio Integration Status & Settings */}
        {canManagePbx && isConfigured && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsTwilioSettingsOpen(true)}
            className="w-fit gap-2"
          >
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span className="text-sm"><span key={tUi("twilioConnected")}>{tUi("twilioConnected")}</span></span>
            <Settings className="w-4 h-4 text-muted-foreground" />
          </Button>
        )}
      </div>

      {!isConfigured && (
        <TwilioConnectionBanner
          canConnect={canManagePbx}
          onConnect={() => setIsTwilioSettingsOpen(true)}
        />
      )}

      {/* Tabs */}
      <TooltipProvider delayDuration={700}>
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <TabsList>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex h-full">
                      <TabsTrigger value="overview"><span key={tUi("overview")}>{tUi("overview")}</span></TabsTrigger>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <span key={tUi("generalViewAndQuickActions")}>{tUi("generalViewAndQuickActions")}</span></TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex h-full">
                      <TabsTrigger value="numbers"><span key={tUi("numbers")}>{tUi("numbers")}</span></TabsTrigger>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <span key={tUi("allowsToImportTwilioAndExternalSIPNumbers")}>{tUi("allowsToImportTwilioAndExternalSIPNumbers")}</span></TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex h-full">
                      <TabsTrigger value="flows"><span key={tUi("inboundCallFlows")}>{tUi("inboundCallFlows")}</span></TabsTrigger>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <span key={tUi("buildRoutingFlowsForIncomingCallsIncludingAI")}>{tUi("buildRoutingFlowsForIncomingCallsIncludingAI")}</span></TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex h-full">
                      <TabsTrigger value="operators"><span key={tUi("operatorGroups")}>{tUi("operatorGroups")}</span></TabsTrigger>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <span key={tUi("createTeamsOfHumanOperatorsThatInboundFlows")}>{tUi("createTeamsOfHumanOperatorsThatInboundFlows")}</span></TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex h-full">
                      <TabsTrigger value="voicemails"><span key={tUi("voicemails")}>{tUi("voicemails")}</span></TabsTrigger>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <span key={tUi("reviewVoicemailRecordingsAndMessagesCapturedWhenCalls")}>{tUi("reviewVoicemailRecordingsAndMessagesCapturedWhenCalls")}</span></TooltipContent>
                </Tooltip>
              </TabsList>
            </div>

            {activeTab === 'flows' && canManagePbx && (
              <Button onClick={handleNewFlow} className="w-full sm:w-fit">
                <Plus className="w-4 h-4 mr-2" />
                <span><span key={tUi("newInboundFlow")}>{tUi("newInboundFlow")}</span></span>
              </Button>
            )}

            {activeTab === 'operators' && canManagePbx && (
              <Button onClick={handleNewOperatorGroup} className="w-full sm:w-fit">
                <Plus className="w-4 h-4 mr-2" />
                <span><span key={tUi("newGroup")}>{tUi("newGroup")}</span></span>
              </Button>
            )}
          </div>

          <TabsContent value="overview" className="mt-6">
            <OverviewTab
              key={integrationKey}
              onNavigateToTab={handleTabChange}
              onEditFlow={handleEditFlow}
              onNewFlow={handleNewFlow}
              onNewCampaign={canManageCampaigns && !campaignsLocked ? handleNewCampaign : undefined}
              onMakeCall={canManageCampaigns ? handleMakeCall : undefined}
              onImportExternalSip={handleImportExternalSip}
              onViewAllCalls={handleViewAllCalls}
            />
          </TabsContent>

          <TabsContent value="numbers" className="mt-6">
            <PhoneNumbersTab key={integrationKey} canManage={canManagePbx || canViewIntegrations} />
          </TabsContent>

          <TabsContent value="flows" className="mt-6">
            <CallFlowsTab
              key={integrationKey}
              canManage={canManagePbx}
              onEditFlow={handleEditFlow}
              onNewFlow={handleNewFlow}
            />
          </TabsContent>

          <TabsContent value="operators" className="mt-6">
            <OperatorGroupsTab
              key={integrationKey}
              canManage={canManagePbx}
              createRequestId={operatorGroupCreateRequestId}
            />
          </TabsContent>

          <TabsContent value="voicemails" className="mt-6">
            <VoicemailsTab key={integrationKey} canManage={canManagePbx} />
          </TabsContent>
        </Tabs>
      </TooltipProvider>

      {/* Twilio Settings Dialog */}
      <Dialog open={isTwilioSettingsOpen} onOpenChange={setIsTwilioSettingsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              <span key={isConfigured ? tUi("twilioConfiguration") : tUi("connectTwilio")}>{isConfigured ? tUi("twilioConfiguration") : tUi("connectTwilio")}</span>
            </DialogTitle>
          </DialogHeader>
          <TwilioCredentialsForm
            onSave={handleTwilioConfigured}
            onCancel={() => setIsTwilioSettingsOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <OutgoingCallDialog
        agent={null}
        agents={voiceAgents}
        isLoadingAgents={isLoadingVoiceAgents}
        agentsError={voiceAgentsError}
        open={isOutgoingCallOpen}
        onOpenChange={setIsOutgoingCallOpen}
      />
    </div>
  )
}
